import { hashKey, useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { createGetColor } from 'src/components/Listings/InventoryEventPage/utils/colorUtils';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { useGetActiveVenueZoneMapSectionGroups } from 'src/contexts/EventMapContext/useGetActiveVenueZoneMapSectionGroups';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { useComparableListingsVenueMapColor } from 'src/hooks/useComparableListingsVenueMapColor';
import { useDebouncedValueSafe } from 'src/hooks/useDebouncedValueSafe';
import { useEventVenueMapSectionHandler } from 'src/hooks/useEventVenueMapSectionHandler';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { quantityFiltersFromJson } from 'src/utils/autoPricingUtils';
import { posChangedField } from 'src/utils/posFieldUtils';
import { queryWithTimeout } from 'src/utils/queryUtils';
import { onSectionSelctionWithMyRowDefaults } from 'src/utils/venueUtils';
import {
  BulkEditListingClient,
  CompListing,
  Event,
  Feature,
  Listing,
  ListingDetailsAutoPricingSectionUpdatesV2,
  ListingPriceCalculationsInEventInput,
  NetProceedsFloorAdjustment,
  PosFieldOfSelectedSectionSettings,
  SelectedSectionSettings,
  UiMoney,
} from 'src/WebApiController';

export const useBulkGetListingPriceCalculationsInEvent = (
  event: Event,
  selectedListings: Listing[]
) => {
  const { activeAccountWebClientConfig, loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();
  const { trackError } = useClientLogContext();

  const uiCurrency = useMemo(
    () => getUiCurrency(loginContext?.user?.activeAccount?.currencyCode),
    [getUiCurrency, loginContext?.user?.activeAccount?.currencyCode]
  );

  const listingIds = useMemo(
    () => selectedListings.map((l) => l.id),
    [selectedListings]
  );

  const { watch } = useFormContext<ListingDetailsAutoPricingSectionUpdatesV2>();
  const formValues = watch();

  const selectedSectionSettings: PosFieldOfSelectedSectionSettings | null =
    useMemo(() => {
      if (formValues.deriveCompsFromVenueZoneConfig) {
        return {
          value: {
            sectionIdFilterFromZoneMap: true,
            includeBetterZones: formValues.includeBetterZones,
            rowOffset: formValues.rowCompOffset,
          } as SelectedSectionSettings,
          hasChanged: true,
        };
      }

      if (
        formValues.includeExistingSelectedSections &&
        !formValues.compListingSelectedSectionSettings?.value &&
        formValues.compListingSelectedSectionSettings?.hasChanged
      ) {
        // If we're including existing sections but no custom sections are set,
        // we need to have. a non-null value here so that the bulk preview works
        // as intended
        return {
          value: {} as SelectedSectionSettings,
          hasChanged: true,
        };
      }

      return formValues.compListingSelectedSectionSettings;
    }, [
      formValues.compListingSelectedSectionSettings,
      formValues.deriveCompsFromVenueZoneConfig,
      formValues.includeBetterZones,
      formValues.includeExistingSelectedSections,
      formValues.rowCompOffset,
    ]);

  const maxVelocityCircuitBreakerStateRowVersion = useMemo(() => {
    const rowVersions = selectedListings
      .map((listing) => listing.cirBrSuppRowVer)
      .filter((version) => version !== null && version !== undefined);

    return rowVersions.length ? Math.max(...rowVersions) : null;
  }, [selectedListings]);

  // TODO: The bulk edit form should just be ListingPriceCalculationsInEventInput directly
  const autoPricingSettingsUpdates: ListingPriceCalculationsInEventInput =
    useMemo(
      () => ({
        compListingSetting: {
          mode: formValues.compListingMode,
          selectedSectionSettings: selectedSectionSettings,
          includeExistingSelectedSectionSettings: {
            value: formValues.includeExistingSelectedSections,
            hasChanged: formValues.includeExistingSelectedSections !== null,
          },
          onlyForSelectedSectionsEnabled:
            formValues.compListingOnlyForSelectedSectionsEnabled,
          quantityFilters:
            formValues.compListingQuantityScoreAdjustmentOverrideJson
              ? {
                  value: quantityFiltersFromJson(
                    formValues.compListingQuantityScoreAdjustmentOverrideJson
                      .value
                  ),
                  hasChanged:
                    formValues.compListingQuantityScoreAdjustmentOverrideJson
                      .hasChanged,
                }
              : null,
          excludeFanInventory: formValues.compListingExcludeFanInventory,
          excludeDefects: formValues.compListingExcludeDefects,
        },
        underCutSetting: {
          mode: formValues.undercutMode,
          referenceListingPosition: formValues.undercutReferenceListingPosition,
          underCutAbsoluteAmount: formValues.undercutAbsoluteAmount
            ? {
                value:
                  formValues.undercutAbsoluteAmount.value != null
                    ? ({
                        amt: formValues.undercutAbsoluteAmount.value,
                        currency: uiCurrency.code,
                      } as UiMoney)
                    : null,
                hasChanged: formValues.undercutAbsoluteAmount.hasChanged,
              }
            : null,
          underCutRelativeAmount: formValues.undercutRelativeAmount,
        },
        outlierSetting: {
          mode: formValues.outlierMode,
          standardDeviations: formValues.outlierStandardDeviations,
          outlierLimit: formValues.outlierKthLowestLimit,
          absoluteOutlierSpacing:
            formValues.outlierKthLowestLimitAbsoluteSpacing
              ? {
                  value:
                    formValues.outlierKthLowestLimitAbsoluteSpacing.value !=
                    null
                      ? ({
                          amt: formValues.outlierKthLowestLimitAbsoluteSpacing
                            .value,
                          currency: uiCurrency.code,
                        } as UiMoney)
                      : null,
                  hasChanged:
                    formValues.outlierKthLowestLimitAbsoluteSpacing.hasChanged,
                }
              : null,
          relativeOutlierSpacing:
            formValues.outlierKthLowestLimitRelativeSpacing,
        },
        netProceedsFloor: formValues.netProceedsFloor?.hasChanged
          ? {
              value: {
                absoluteAmount:
                  formValues.netProceedsFloor?.value != null
                    ? ({
                        amt: formValues.netProceedsFloor.value,
                        currency: uiCurrency.code,
                      } as UiMoney)
                    : null,
                relativeAdjustmentType: null,
                relativeAmount: null,
                relativePercentage: null,
              } as NetProceedsFloorAdjustment,
              hasChanged: true,
            }
          : formValues.netProceedsFloorAdjustmentAmount != null &&
              formValues.netProceedsFloorAdjustmentRelative != null &&
              !!formValues.netProceedsFloorAdjustmentType
            ? {
                value: {
                  absoluteAmount: null,
                  relativeAmount: !formValues.netProceedsFloorAdjustmentRelative
                    ? {
                        amt: formValues.netProceedsFloorAdjustmentAmount,
                        currency: uiCurrency.code,
                      }
                    : null,
                  relativePercentage:
                    formValues.netProceedsFloorAdjustmentRelative
                      ? formValues.netProceedsFloorAdjustmentAmount
                      : null,
                  relativeAdjustmentType:
                    formValues.netProceedsFloorAdjustmentType,
                } as NetProceedsFloorAdjustment,
                hasChanged: true,
              }
            : null,
        netProceedsCeiling: formValues.netProceedsCeiling
          ? {
              value:
                formValues.netProceedsCeiling.value != null
                  ? ({
                      amt: formValues.netProceedsCeiling.value,
                      currency: uiCurrency.code,
                    } as UiMoney)
                  : null,
              hasChanged: formValues.netProceedsCeiling.hasChanged,
            }
          : null,
        rankPremium: null,
        velocityCircuitBreakerStateMaxRowVersion:
          maxVelocityCircuitBreakerStateRowVersion,
      }),
      [
        formValues.compListingMode,
        formValues.includeExistingSelectedSections,
        formValues.compListingOnlyForSelectedSectionsEnabled,
        formValues.compListingQuantityScoreAdjustmentOverrideJson,
        formValues.compListingExcludeFanInventory,
        formValues.compListingExcludeDefects,
        formValues.undercutMode,
        formValues.undercutReferenceListingPosition,
        formValues.undercutAbsoluteAmount,
        formValues.undercutRelativeAmount,
        formValues.outlierMode,
        formValues.outlierStandardDeviations,
        formValues.outlierKthLowestLimit,
        formValues.outlierKthLowestLimitAbsoluteSpacing,
        formValues.outlierKthLowestLimitRelativeSpacing,
        formValues.netProceedsFloor?.hasChanged,
        formValues.netProceedsFloor?.value,
        formValues.netProceedsFloorAdjustmentAmount,
        formValues.netProceedsFloorAdjustmentRelative,
        formValues.netProceedsFloorAdjustmentType,
        formValues.netProceedsCeiling,
        selectedSectionSettings,
        uiCurrency.code,
        maxVelocityCircuitBreakerStateRowVersion,
      ]
    );

  const [debouncedPricingSettingsUpdate] = useDebouncedValueSafe(
    autoPricingSettingsUpdates
  );

  const shouldQuery = Boolean(
    activeAccountWebClientConfig.activeAccountId &&
      event.viagId &&
      !isEmpty(listingIds) &&
      debouncedPricingSettingsUpdate
  );
  const listingCalculationsQuery = useQuery({
    queryKey: [
      'BulkEditListingClient.BulkGetListingPriceCalculationsInEvent',
      activeAccountWebClientConfig.activeAccountId,
      event.viagId,
      hashKey([listingIds, debouncedPricingSettingsUpdate]),
    ],
    queryFn: async () => {
      const abortController = new AbortController();
      return await queryWithTimeout(
        new BulkEditListingClient(
          activeAccountWebClientConfig
        ).bulkGetListingPriceCalculationsInEvent(
          event.viagId!,
          {
            item1: listingIds,
            item2: autoPricingSettingsUpdates!,
          },
          abortController.signal
        ),
        (e: Error) => {
          abortController.abort(e.message);
        }
      );
    },
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(500 * attemptIndex, 3000),
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    // The response of the price calculation is huge
    // We don't want to cache the data and eat up the heap mem as the query is per input
    gcTime: 0,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('PricingClient.getListingAutoPricingSettings', error, {
          trackErrorData: {
            activeAccountId: activeAccountWebClientConfig.activeAccountId,
            viagogoEventId: event.viagId,
            listingIds,
            autoPricingSettingsUpdates,
          },
        });
      },
    },
  });

  return {
    listingCalculationsQuery,
    bulkListingPriceCalculationResult: listingCalculationsQuery.data,
    isLoading: listingCalculationsQuery.isLoading,
  };
};

export const useBulkEditAutoPricingSettingsSidePanelEventMapProps = (
  viagogoEventId: number,
  listings: Listing[],
  compListings: CompListing[] | undefined
) => {
  const hasDefaultToMyRowOnSectionSelectionFeature = useUserHasFeature(
    Feature.DefaultToMyRowOnSectionSelection
  );
  const { venueMapInfo } = useEventMapContext();
  const { sectionGroupsBySectionId } =
    useGetActiveVenueZoneMapSectionGroups(viagogoEventId);

  const { setValue, watch } =
    useFormContext<ListingDetailsAutoPricingSectionUpdatesV2>();
  const { compListingSelectedSectionSettings, deriveCompsFromVenueZoneConfig } =
    watch();

  const selectedSectionIds = useMemo(
    () =>
      deriveCompsFromVenueZoneConfig
        ? []
        : (compListingSelectedSectionSettings?.value?.sectionIdFilter ??
          undefined),
    [
      compListingSelectedSectionSettings?.value?.sectionIdFilter,
      deriveCompsFromVenueZoneConfig,
    ]
  );

  const setSelectedSectionIds = useCallback(
    (sectionIds: number[]) => {
      let compListingSelectedSectionSettingsNew: SelectedSectionSettings | null =
        null;
      if (hasDefaultToMyRowOnSectionSelectionFeature) {
        const selectedSections =
          venueMapInfo?.sections?.filter(({ id }) => sectionIds.includes(id)) ??
          [];
        const selectedSectionSettingsWithRowDefault =
          onSectionSelctionWithMyRowDefaults(
            compListingSelectedSectionSettings?.value ?? null,
            selectedSections,
            venueMapInfo?.sections ?? []
          );
        compListingSelectedSectionSettingsNew = {
          ...selectedSectionSettingsWithRowDefault,
          sectionFilterMode: null,
        };
      } else {
        compListingSelectedSectionSettingsNew = {
          ...compListingSelectedSectionSettings?.value,
          sectionIdFilter: sectionIds,
          rowIdFilter:
            compListingSelectedSectionSettings?.value?.rowIdFilter ?? [],
          sectionRowIdFilter:
            compListingSelectedSectionSettings?.value?.sectionRowIdFilter ?? {},
          sectionIdFilterFromZoneMap: false,
          includeBetterZones: false,
          rowOffset: null,
          sectionFilterMode: null,
        };
      }

      setValue(
        'compListingSelectedSectionSettings',
        posChangedField(compListingSelectedSectionSettingsNew)
      );
    },
    [
      compListingSelectedSectionSettings?.value,
      hasDefaultToMyRowOnSectionSelectionFeature,
      setValue,
      venueMapInfo?.sections,
    ]
  );

  const { onSectionClicked, onToggleMirrors, setSelectedSections } =
    useEventVenueMapSectionHandler(
      selectedSectionIds,
      setSelectedSectionIds,
      venueMapInfo
    );

  const compListingsVenueMapColor = useComparableListingsVenueMapColor(
    listings,
    undefined,
    compListings
  );

  const getColor = useMemo(
    () =>
      deriveCompsFromVenueZoneConfig
        ? createGetColor(sectionGroupsBySectionId)
        : compListingsVenueMapColor,
    [
      compListingsVenueMapColor,
      deriveCompsFromVenueZoneConfig,
      sectionGroupsBySectionId,
    ]
  );

  return {
    getColor,
    onSectionClicked,
    onToggleMirrors,
    selectedSectionIds,
    setSelectedSections,
    disableHoverEffect: Boolean(deriveCompsFromVenueZoneConfig),
  };
};
